<template>
  <div class="search-panel">
    <div class="search-panel__container">
      <div class="search-panel__type">
        <div class="search-panel__type-select">
          <label class="search-panel__type-select-label">
            TYPE
          </label>
          <multiselect
            v-model="selectedTypeModel"
            class="select-transparent"
            track-by="id"
            label="label"
            :options="searchTypeOptions"
            :searchable="false"
            :allow-empty="false"
            :show-labels="false"
          />
        </div>
      </div>
      <div class="search-panel__input">
        <multiselect
          ref="searchItem"
          v-model="selectedItemModel"
          class="select-search-item"
          track-by="id"
          label="label"
          :options="searchOptions"
          :loading="areOptionsLoading"
          :internal-search="false"
          :clear-on-select="false"
          :preserve-search="true"
          :allow-empty="false"
          :show-labels="false"
          placeholder="Search..."
          @search-change="onSearch"
          @input="onItemChange"
        >
          <template slot="clear">
            <div
              v-if="selectedItem"
              class="multiselect__clear"
              @mousedown.prevent.stop="clear"
            />
          </template>
          <span slot="noResult">No cities or hotels found. Consider changing the search query.</span>
          <span slot="noOptions">Start typing to search for cities or hotels...</span>
          <template
            slot="option"
            slot-scope="{ option }"
          >
            <div
              class="select-search-item__option"
              :class="{
                [`type-${option.type}`]: true,
              }"
            >
              <span class="select-search-item__option-icon">
                <img
                  v-if="option.type === 'city'"
                  :src="require('@/assets/icons/icon-location.svg')"
                  alt=""
                  width="10"
                  height="15"
                >
                <img
                  v-if="option.type === 'hotel'"
                  :src="require('@/assets/icons/icon-hotel.svg')"
                  alt=""
                  width="15"
                  height="10"
                >
              </span>
              <span class="select-search-item__option-title">
                {{ option.label }}
              </span>
            </div>
          </template>
        </multiselect>
      </div>
      <div class="search-panel__actions">
        <button
          class="btn"
          @click="submit"
        >
          Search
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { searchTypeOptions } from '@/helpers';

export default {
  name: 'search-panel',
  props: {
    searchType: {
      type: String,
      default: searchTypeOptions[0].id,
    },
    searchItem: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      searchTypeOptions,
      cities: null,
      hotels: null,
      areOptionsLoading: false,
      selectedItem: this.searchItem,
      selectedType: this.searchType,
    };
  },
  computed: {
    searchOptions() {
      if (!this.cities && !this.hotels) {
        return [];
      }
      const cities = this.cities ? this.cities.map(city => ({
        id: city.toLowerCase(),
        label: city,
        type: 'city',
      })) : [];
      const hotels = this.hotels ? this.hotels.map(({ displayName, hotelId }) => ({
        id: hotelId,
        label: displayName,
        type: 'hotel',
      })) : [];
      return [
        ...cities,
        ...hotels,
      ];
    },
    selectedTypeModel: {
      get() {
        if (!this.selectedType) return null;
        return this.searchTypeOptions.find(({ id }) => id === this.selectedType) || null;
      },
      set({ id }) {
        this.selectedType = id;
      },
    },
    selectedItemModel: {
      get() {
        if (!this.selectedItem) return null;
        return this.searchOptions.find(({ id }) => id === this.selectedItem) || null;
      },
      set({ id }) {
        this.selectedItem = id;
      },
    },
  },
  async created() {
    if (this.searchItem && this.searchItem.length > 0) {
      await this.onSearch(this.searchItem);
      if (this.selectedItemModel) {
        this.$refs.searchItem.search = this.selectedItemModel.label;
        this.$emit('city', this.selectedItemModel);
      } else {
        this.$emit('clear', ['city']);
      }
    }
  },
  methods: {
    ...mapActions([
      'getCities',
      'getHotelsShort',
    ]),
    submit() {
      const payload = {
        type: this.selectedTypeModel,
      };
      if (this.selectedItemModel && this.selectedItemModel.type === 'city') {
        payload.city = this.selectedItemModel;
      }
      if (this.selectedItemModel && this.selectedItemModel.type === 'hotel') {
        payload.hotel = this.selectedItemModel;
      }
      this.$emit('submit', payload);
    },
    onItemChange(val) {
      this.$refs.searchItem.search = val.label;
      if (val.type === 'hotel') {
        this.$router.push({
          name: 'content',
          params: {
            id: val.id,
          },
        });
      }
    },
    async onSearch(query) {
      this.areOptionsLoading = true;
      const [
        cities,
        hotels,
      ] = await Promise.all([
        this.getCities(),
        this.getHotelsShort(),
      ]);
      this.cities = cities.filter(city => city.toLowerCase().indexOf(query.toLowerCase()) > -1);
      this.hotels = hotels.filter(({ displayName, cityName }) => {
        if (displayName.toLowerCase().indexOf(query.toLowerCase()) > -1) return true;
        if (cityName.toLowerCase().indexOf(query.toLowerCase()) > -1) return true;
        return false;
      });
      this.areOptionsLoading = false;
    },
    clear() {
      this.selectedItem = null;
      this.$refs.searchItem.search = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.search-panel {
  background: #f7f9fc;
  color: #bdc4ce;
  padding: 20px 45px;
  @media screen and (max-width: 1279px) {
    padding: 15px 35px;
  }
  @media screen and (max-width: 1023px) {
    padding: 10px 25px;
  }
  @media screen and (max-width: 768px) {
    padding: 10px;
  }
  &__container {
    display: flex;
    justify-content: space-between;
  }
  &__type {
    border-right: 2px solid rgba(#6e7c91, 0.07);
    width: 150px;
    padding: 30px 39px 30px 0;
    @media screen and (max-width: 1279px) {
      padding: 25px 30px 25px 0;
    }
    @media screen and (max-width: 1023px) {
      padding: 20px 25px 20px 0;
    }
    @media screen and (max-width: 768px) {
      padding-right: 10px;
    }
    &-select {
      position: relative;
      &-label {
        font-size: 10px;
        position: absolute;
        left: 0;
        bottom: 100%;
      }
    }
    &-label {
      font-size: 10px;
      color: #6e7c91;
    }
  }
  &__input {
    flex-grow: 1;
    align-self: center;
    padding: 20px;
    @media screen and (max-width: 1023px) {
      padding: 15px;
    }
  }
  &__actions {
    align-self: center;
    padding: 20px 0;
    @media screen and (max-width: 1023px) {
      padding: 15px;
    }
  }
}
</style>
